import { useCallback, useEffect, useState } from 'react';
// @mui
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
// hooks
import { useDebounce } from 'src/hooks/use-debounce';
// _mock
import { POST_SORT_OPTIONS } from 'src/_mock';
// api
import { useSearchPosts } from 'src/api/blog';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
// types
import { IPostFilters, IPostFilterValue } from 'src/types/notification';
//
import ArticleSort from '../article-sort';
import ArticleSearch from '../article-search';
import NewsletterListHorizontal from '../notification-list-horizontal';
import { useLocales } from '../../../locales';
import { useDispatch, useSelector } from '../../../redux/store';
import { Direction, PushNotificationStatus } from '../../../api';
import { getPushNotifications } from '../../../redux/slices/push-notification';

// ----------------------------------------------------------------------

const defaultFilters: IPostFilters = {
  status: 'all',
};

// ----------------------------------------------------------------------

export default function NotificationListView() {
  const settings = useSettingsContext();

  const { t } = useLocales();

  const [sortBy, setSortBy] = useState('sendingTime');

  const [filters, setFilters] = useState(defaultFilters);

  const [searchQuery, setSearchQuery] = useState('');

  const debouncedQuery = useDebounce(searchQuery);

  const dispatch = useDispatch();

  const selectedGym = useSelector((state) => state.gym.globalGym);

  const isLoading = useSelector((state) => state.newsletter.isLoading);

  const tableData = useSelector((state) => state.pushNotification.notifications);

  const tableStats = useSelector((state) => state.pushNotification.stats);

  const STATUS_OPTIONS = [
    { value: 'all', label: t('All'), color: 'default', count: tableStats.total },
    {
      value: PushNotificationStatus.Delivered,
      label: t(PushNotificationStatus.Delivered),
      color: 'success',
      count: tableStats.delivered,
    },
    {
      value: PushNotificationStatus.Planned,
      label: t(PushNotificationStatus.Planned),
      color: 'info',
      count: tableStats.planned,
    },
    {
      value: PushNotificationStatus.Pending,
      label: t(PushNotificationStatus.Pending),
      color: 'warning',
      count: tableStats.pending,
    },
    {
      value: PushNotificationStatus.Failure,
      label: t(PushNotificationStatus.Failure),
      color: 'error',
      count: tableStats.failure,
    },
  ] as const;

  useEffect(() => {
    dispatch(
      getPushNotifications({
        pageSize: 10,
        pageNumber: 0,
        sortBy: [sortBy],
        direction: Direction.Desc,
        status: filters.status === 'all' ? null : filters.status,
      })
    );
  }, [dispatch, filters, sortBy, selectedGym]);

  const { searchResults, searchLoading } = useSearchPosts(debouncedQuery);

  const handleSortBy = useCallback((newValue: string) => {
    setSortBy(newValue);
  }, []);

  const handleFilters = useCallback((name: string, value: IPostFilterValue) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const handleSearch = useCallback((inputValue: string) => {
    setSearchQuery(inputValue);
  }, []);

  const handleFilterPublish = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      handleFilters('status', newValue);
    },
    [handleFilters]
  );

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <CustomBreadcrumbs
        heading="List"
        links={[
          {
            name: t('Dashboard'),
            href: paths.dashboard.root,
          },
          {
            name: t('Notification'),
            href: paths.newsletter.root,
          },
          {
            name: t('List'),
          },
        ]}
        action={
          <Button
            component={RouterLink}
            href={paths.notification.new}
            variant="contained"
            color="primary"
            startIcon={<Iconify icon="mingcute:add-line" />}
          >
            {t('New notification')}
          </Button>
        }
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      />

      <Stack
        spacing={3}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-end', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      >
        <ArticleSearch
          query={debouncedQuery}
          results={searchResults}
          onSearch={handleSearch}
          loading={searchLoading}
          hrefItem={(title: string) => paths.dashboard.post.details(title)}
        />

        <ArticleSort sort={sortBy} onSort={handleSortBy} sortOptions={POST_SORT_OPTIONS} />
      </Stack>

      <Tabs
        value={filters.status}
        onChange={handleFilterPublish}
        sx={{
          mb: { xs: 3, md: 5 },
        }}
      >
        {STATUS_OPTIONS.map((tab) => (
          <Tab
            key={tab.value}
            iconPosition="end"
            value={tab.value}
            label={tab.label}
            icon={
              <Label
                variant={
                  ((tab.value === 'all' || tab.value === filters.status) && 'filled') || 'soft'
                }
                color={tab.color}
              >
                {tab.count}
              </Label>
            }
            sx={{ textTransform: 'capitalize' }}
          />
        ))}
      </Tabs>

      <NewsletterListHorizontal notifications={tableData} loading={isLoading} />
    </Container>
  );
}
